import React, { useCallback, useMemo, useRef, useState } from "react";

type PresetType = "pdf" | "excel" | "word";

interface UploadFileDropzoneProps {
  label?: string;
  required?: boolean;
  maxSizeMB?: number;
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  existingUrl?: string | null;
  types?: (PresetType | string)[];
}

const PRESET_MIMES: Record<PresetType, string[]> = {
  pdf: ["application/pdf"],
  excel: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ],
  word: [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};

const PRESET_EXTS: Record<PresetType, string[]> = {
  pdf: [".pdf"],
  excel: [".xls", ".xlsx"],
  word: [".doc", ".docx"],
};

function normalizeTypes(types?: (PresetType | string)[]) {
  if (!types || types.length === 0)
    return { mimes: [] as string[], exts: [] as string[] };
  const mimes: string[] = [];
  const exts: string[] = [];
  for (const t of types) {
    if ((["pdf", "excel", "word"] as PresetType[]).includes(t as PresetType)) {
      const key = t as PresetType;
      mimes.push(...PRESET_MIMES[key]);
      exts.push(...PRESET_EXTS[key]);
    } else if (typeof t === "string") {
      if (t.startsWith(".")) exts.push(t.toLowerCase());
      else mimes.push(t.toLowerCase());
    }
  }
  return { mimes: Array.from(new Set(mimes)), exts: Array.from(new Set(exts)) };
}

function humanizeTypes(types?: (PresetType | string)[]) {
  if (!types || types.length === 0) return "orice format";
  return types
    .map((t) =>
      String(t)
        .replace("application/", "")
        .replace("vnd.", "")
        .replace(/[.-]/g, " ")
        .toUpperCase(),
    )
    .join(", ");
}

const UploadFileDropzone: React.FC<UploadFileDropzoneProps> = ({
  label = "Fișier",
  required = false,
  maxSizeMB = 20,
  value,
  onChange,
  error,
  disabled = false,
  className = "",
  existingUrl = null,
  types = [],
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { mimes, exts } = useMemo(() => normalizeTypes(types), [types]);
  const acceptAttr: string | undefined = useMemo(() => {
    if (mimes.length === 0 && exts.length === 0) return undefined;
    return [...mimes, ...exts].join(",");
  }, [mimes, exts]);
  const allowedHuman = useMemo(() => humanizeTypes(types), [types]);

  const prevent = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(true);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(false);
  };

  const validateFile = (f: File | null): string | null => {
    if (!f) return null;
    if (f.size > maxSizeMB * 1024 * 1024)
      return `Fișier prea mare (max ${maxSizeMB}MB).`;
    if (mimes.length === 0 && exts.length === 0) return null;

    const typeOk = mimes.length ? mimes.includes(f.type) : true;
    const name = f.name.toLowerCase();
    const extOk = exts.length ? exts.some((ext) => name.endsWith(ext)) : true;

    if (!typeOk && !extOk) return `Tip neacceptat. Permise: ${allowedHuman}.`;
    return null;
  };

  const handleIncomingFile = (f: File | null) => {
    const msg = validateFile(f);
    setLocalError(msg);
    onChange(msg ? null : f);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      prevent(e);
      if (disabled) return;
      setIsDragging(false);
      const f = e.dataTransfer.files?.[0] ?? null;
      handleIncomingFile(f);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled],
  );

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const borderColor =
    error || localError
      ? "border-danger"
      : isDragging
      ? "border-primary"
      : "border-secondary";

  const zoneClasses = [
    "rounded-3",
    "p-3",
    "position-relative",
    "d-flex",
    "align-items-center",
    "justify-content-between",
    "gap-3",
    borderColor,
    disabled
      ? "bg-light"
      : isDragging
      ? "bg-primary-subtle"
      : "bg-body-tertiary",
    "user-select-none",
  ].join(" ");

  return (
    <div className={className}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="form-label m-0">{label}</span>
        {required && (
          <span className="badge bg-light text-body-secondary">
            obligatoriu
          </span>
        )}
      </div>

      <div
        className={zoneClasses}
        style={{
          borderStyle: "dashed",
          borderWidth: "2px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={openPicker}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
        aria-disabled={disabled}
        role="button">
        <div className="flex-grow-1">
          <div className="fw-semibold">
            Trage fișierul aici sau <u>alege din calculator</u>
          </div>
          <div className="small text-muted">
            Formate acceptate: {allowedHuman}. Maxim {maxSizeMB}MB.
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="d-none"
          disabled={disabled}
          onChange={(e) =>
            handleIncomingFile(e.currentTarget.files?.[0] ?? null)
          }
        />

        <div className="text-end" style={{ minWidth: 160, maxWidth: 280 }}>
          {value ? (
            <>
              <div className="fw-semibold text-truncate" title={value.name}>
                {value.name}
              </div>
              <div className="small text-muted">
                {(value.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </>
          ) : (
            <div className="small text-muted">Niciun fișier selectat</div>
          )}
        </div>
      </div>

      {existingUrl && (
        <div className="mt-2 small">
          Document existent:{" "}
          <a
            href={existingUrl}
            target="_blank"
            rel="noreferrer"
            className="link-underline">
            deschide
          </a>
        </div>
      )}

      {(error || localError) && (
        <div className="text-danger mt-2 small">{error ?? localError}</div>
      )}
    </div>
  );
};

export default UploadFileDropzone;
